import React, { useLayoutEffect, useRef, useState } from 'react';
import { css } from '@emotion/react';
import { ChevronLeftOutlineIcon, ChevronRightOutlineIcon } from '@applyboard/ui-icons';

import { Heading, HeadingProps } from '../Heading';
import { SrOnly } from '../SrOnly';

import { hasSymbol, useId, useCurrentBreakpoint } from '~/utils';
import { useFullTheme } from '~/design';

import { useButtonBaseStyle, useButtonEmphasisStyle } from '~/internal/InternalButton';
import { useContextOrThrow } from '~/utils/useContextOrThrow';
import { IconWrapper } from '~/internal';

const BTN_DIMENSION = 40;

const symbolMap = {
  Heading: Symbol('Carousel.Heading'),
} as const;

interface CarouselContextProps {
  currentSlide: number;
  headingId: string;
  itemGap: number;
  itemsPerSlide: number;
  navButtonPlacement: CarouselProps['navButtonPlacement'];
  setCurrentSlide: (value: number) => void;
  slideLength: number;
}

const CarouselContext = React.createContext<CarouselContextProps | null>(null);

export interface CarouselProps {
  /** The aria-label of the carousel */
  'aria-label'?: string;

  /** The aria-labelledby of the carousel */
  'aria-labelledby'?: string;

  /** The carousel content. */
  children: React.ReactNode;

  /** Gap between each carousel item */
  itemGap?: number;

  /** How many items are visible per slide at a time. For accessibilty concerns, small displays will
   * only show one slide at a time. */
  itemsPerSlide?: number;

  /** Where will the back/forward buttons be placed. Useful alongside Bleed for carousels that bleed
   * outside the page layout. */
  navButtonPlacement?: 'inside' | 'outside';

  /**
   * The current slide index. Only required if you want to control the
   * slide index from outside the component
   */
  slide?: number;

  /**
   * Callback that is called when the user wants to change the slide
   */
  onChangeSlide?: (slide: number) => void;
}

/**
 * Carousels show a collection of items one at a time. They are also known as “slideshows” and “sliders”.
 */
export function Carousel({
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledby,
  children,
  itemGap = 12,
  itemsPerSlide: _itemsPerSlide = 3,
  navButtonPlacement = 'outside',
  slide,
  onChangeSlide,
}: CarouselProps) {
  const { colors } = useFullTheme();

  const headingId = `${useId()}-heading`;
  const descId = `${useId()}-desc`;

  const ref = useRef<HTMLDivElement>(null);

  const childrenArray = React.Children.toArray(children);
  const headingChild = childrenArray.find(child => hasSymbol(child, symbolMap.Heading));
  const otherChildren = childrenArray.filter(child => !hasSymbol(child, symbolMap.Heading));

  if (!!ariaLabel && !!headingChild) {
    throw new Error('Cannot use `aria-label` in conjunction with Carousel.Heading.');
  } else if (!!ariaLabel && !!ariaLabelledby) {
    throw new Error('Cannot use `aria-label` in conjunction with `aria-labelledby`.');
  } else if (!!ariaLabelledby && !!headingChild) {
    throw new Error('Cannot use `aria-labelledby` in conjunction with Carousel.Heading.');
  }

  if (!ariaLabel && !headingChild && !ariaLabelledby) {
    throw new Error(
      'Either `aria-label` prop, `aria-labelledby` prop, or a `Carousel.Heading` child must be provided.',
    );
  }

  const currentBreakpoint = useCurrentBreakpoint();
  const isExtraSmallScreen = ['xs'].includes(currentBreakpoint ?? 'xs');
  const itemsPerSlide = isExtraSmallScreen ? 1 : _itemsPerSlide;

  const itemLength = otherChildren.length;
  // dots are calculated depending on how many children we have x how many are visible at a time.
  const slideLength = Math.ceil(itemLength / itemsPerSlide);

  const [currentSlideState, setCurrentSlideState] = React.useState(slide || 0);
  const currentSlide = slide ?? currentSlideState;
  const setCurrentSlide = onChangeSlide ? onChangeSlide : setCurrentSlideState;

  const startingVisibleIndex =
    currentSlide === slideLength - 1 ? itemLength - itemsPerSlide : currentSlide * itemsPerSlide;

  const visibleSlideRange = [startingVisibleIndex, startingVisibleIndex + itemsPerSlide - 1];

  // https://react.dev/reference/react/useLayoutEffect#measuring-layout-before-the-browser-repaints-the-screen
  const [containerLeft, setContainerLeft] = useState<number | undefined>(undefined);
  useLayoutEffect(() => {
    if (ref.current) {
      setContainerLeft(ref.current.offsetWidth * currentSlide);
    }
  }, [currentSlide]);

  return (
    <CarouselContext.Provider
      value={{
        currentSlide,
        itemGap,
        itemsPerSlide,
        headingId,
        navButtonPlacement,
        setCurrentSlide,
        slideLength,
      }}
    >
      <section
        aria-label={headingChild ? undefined : ariaLabel}
        aria-labelledby={ariaLabelledby ?? headingChild ? headingId : undefined}
        aria-describedby={headingChild ? descId : undefined}
        css={{
          display: 'flex',
          flexDirection: 'column',
          gap: '24px',
        }}
      >
        {headingChild && (
          <header id={descId}>
            {headingChild}
            <span aria-live="polite">
              <SrOnly>
                Currently showing slide {currentSlide + 1} of {slideLength}
              </SrOnly>
            </span>
          </header>
        )}
        <div
          css={{
            display: 'flex',
            alignItems: 'center',
          }}
        >
          {!headingChild && (
            <SrOnly>
              Currently showing slide {currentSlide + 1} of {slideLength}
            </SrOnly>
          )}
          {slideLength > 1 && (
            <>
              <CarouselButton direction={-1} />
              <CarouselButton direction={1} />
            </>
          )}
          <div
            css={{
              overflow: 'hidden',
              position: 'relative',
              width: '100%',
            }}
            ref={ref}
          >
            <div
              css={{
                display: 'flex',
                position: 'relative',
                left: typeof containerLeft === 'number' ? `-${containerLeft}px` : undefined,
                '@media (prefers-reduced-motion: no-preference)': {
                  transition: 'left 1s',
                },
              }}
            >
              {React.Children.map(otherChildren, (child, index) => {
                const isItemVisible =
                  index >= visibleSlideRange[0] && index <= visibleSlideRange[1];
                return (
                  <div
                    key={`slide-${index}`}
                    css={{
                      flex: `0 0 calc(100% / ${itemsPerSlide})`,
                      padding: `0 ${itemGap / 2}px`,
                      visibility: isItemVisible ? 'visible' : 'hidden',
                      '@media (prefers-reduced-motion: no-preference)': {
                        transitionDelay: isItemVisible ? '0s' : '1s',
                        transitionProperty: 'visibility',
                      },
                    }}
                    role="group"
                    aria-label={`${index + 1} of ${otherChildren.length + 1}`}
                    aria-hidden={!isItemVisible}
                  >
                    {child}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {slideLength > 1 && (
          <div
            css={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '12px',
              placeContent: 'center',
            }}
          >
            {Array(slideLength)
              .fill(null)
              .map((_, index) => (
                <div
                  key={`dot-${index}`}
                  css={{
                    backgroundColor:
                      index === currentSlide ? colors.actionPrimary : colors.bgSecondary,
                    borderRadius: '100%',
                    flex: '0 0 10px',
                    height: '10px',
                    width: '10px',
                  }}
                />
              ))}
          </div>
        )}
      </section>
    </CarouselContext.Provider>
  );
}

interface CarouselButtonProps {
  /** How many slides to move. Negative values will move backwards. */
  direction: number;
}

function CarouselButton({ direction }: CarouselButtonProps) {
  const { currentSlide, itemGap, itemsPerSlide, navButtonPlacement, setCurrentSlide, slideLength } =
    useContextOrThrow(CarouselContext, 'Missing Carousel context.');

  const isForward = direction > 0;

  const baseStyle = useButtonBaseStyle({ intent: 'primary' });
  const emphasisStyle = useButtonEmphasisStyle({ emphasis: 'highlighted', intent: 'secondary' });

  const carouselButtonStyle = css({
    alignItems: 'center',
    display: 'flex',
    flex: `0 0 ${BTN_DIMENSION}px`,
    height: `${BTN_DIMENSION}px`,
    justifyContent: 'center',
    order: isForward ? 3 : undefined,
    zIndex: 1,
  });

  const buttonMarginVal = (BTN_DIMENSION / 2 + itemGap / 2) * -1; // half of btn width + half of item gap

  const carouselButtonPlacement =
    navButtonPlacement == 'inside' &&
    (isForward
      ? css({ marginLeft: `${buttonMarginVal}px` })
      : css({ marginRight: `${buttonMarginVal}px` }));

  const Icon = isForward ? ChevronRightOutlineIcon : ChevronLeftOutlineIcon;

  return (
    <button
      css={[baseStyle, emphasisStyle, carouselButtonStyle, carouselButtonPlacement]}
      aria-label={`${isForward ? 'Next' : 'Previous'} ${
        itemsPerSlide > 1 ? itemsPerSlide + ' items' : 'item'
      }`}
      onClick={() => {
        const nextSlideIndex =
          currentSlide + direction > slideLength - 1
            ? 0
            : currentSlide + direction < 0
              ? slideLength - 1
              : currentSlide + direction;

        setCurrentSlide(nextSlideIndex);
      }}
      type="button"
    >
      <IconWrapper contrast="high" intent="secondary" selector="button">
        <Icon />
      </IconWrapper>
    </button>
  );
}

export type CarouselHeadingProps = Pick<
  HeadingProps,
  'align' | 'children' | 'contrast' | 'intent' | 'level' | 'lineClamp' | 'variant'
>;

Carousel.Heading = function CarouselHeading({
  align = 'start',
  children,
  contrast = 'high',
  intent = 'secondary',
  level,
  lineClamp,
  variant = 'titleM',
}: CarouselHeadingProps) {
  const { headingId } = useContextOrThrow(
    CarouselContext,
    '`Carousel.Heading` can only be rendered inside `Carousel`',
  );

  return (
    <Heading
      level={level}
      align={align}
      contrast={contrast}
      id={headingId}
      intent={intent}
      lineClamp={lineClamp}
      variant={variant}
    >
      {children}
    </Heading>
  );
};

Object.assign(Carousel.Heading, { [symbolMap.Heading]: true });
